<template>
  <BaseModal v-model="open" @update:modelValue="onBackdropClose">
    <div class="cd" role="alertdialog" :aria-labelledby="`cd-title-${uid}`" :aria-describedby="`cd-msg-${uid}`">
      <div class="cd-head">
        <div class="cd-icon" :data-kind="kind">
          <Icon :name="iconFor(kind)" :size="18" />
        </div>
        <h3 :id="`cd-title-${uid}`">{{ title }}</h3>
        <button
          v-if="requireExplicitClose"
          type="button"
          class="cd-x"
          aria-label="Fermer"
          @click="resolve(false)"
        >
          <Icon name="x" :size="16" />
        </button>
      </div>
      <p v-if="message" :id="`cd-msg-${uid}`" class="cd-msg">{{ message }}</p>
      <div v-if="details.length" class="cd-details">
        <div v-for="(d, i) in details" :key="i" class="cd-detail">
          <span class="cd-detail-k">{{ d.k }}</span>
          <span class="cd-detail-v">{{ d.v }}</span>
        </div>
      </div>
      <div v-if="requireText" class="cd-require">
        <label :for="`cd-input-${uid}`">
          {{ requireTextLabel || 'Tapez pour confirmer :' }}
          <b>{{ requireText }}</b>
        </label>
        <input
          :id="`cd-input-${uid}`"
          ref="textInputRef"
          v-model="typed"
          type="text"
          autocomplete="off"
          spellcheck="false"
          @keydown.enter.prevent="canConfirm && resolve(true)"
        />
      </div>
      <div class="cd-actions">
        <button type="button" class="btn-secondary" @click="resolve(false)" ref="cancelBtnRef">
          {{ cancelLabel }}
        </button>
        <button
          type="button"
          :class="['btn-primary', { danger: kind === 'danger' }]"
          :disabled="!canConfirm"
          @click="resolve(true)"
        >
          {{ confirmLabel }}
        </button>
      </div>
    </div>
  </BaseModal>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref, computed, watch, nextTick } from 'vue'
import BaseModal from './BaseModal.vue'
import Icon from './Icon.vue'
import { useConfirmStore, type ConfirmKind } from '@/shared/stores/confirm'

const store = useConfirmStore()
const open = computed({
  get: () => store.isOpen,
  set: (v: boolean) => {
    // En mode « fermeture explicite », un clic à l'extérieur (BaseModal émet
    // false) ne doit PAS fermer : on n'agit que par la croix / Annuler.
    if (!v && !store.current?.requireExplicitClose) store.resolveCurrent(false)
  },
})
const title = computed(() => store.current?.title || '')
const message = computed(() => store.current?.message || '')
const kind = computed<ConfirmKind>(() => store.current?.kind || 'info')
const confirmLabel = computed(() => store.current?.confirmLabel || 'Confirmer')
const cancelLabel = computed(() => store.current?.cancelLabel || 'Annuler')
const requireText = computed(() => store.current?.requireText || '')
const requireTextLabel = computed(() => store.current?.requireTextLabel || '')
const details = computed(() => store.current?.details || [])
const requireExplicitClose = computed(() => !!store.current?.requireExplicitClose)

const typed = ref('')
const textInputRef = ref<HTMLInputElement | null>(null)
// Sans saisie requise → toujours confirmable. Sinon il faut une correspondance
// exacte (insensible aux espaces de début/fin).
const canConfirm = computed(() =>
  !requireText.value || typed.value.trim() === requireText.value.trim(),
)
// Reset + focus du champ à chaque ouverture d'un nouveau dialogue.
watch(() => store.current, (cur) => {
  typed.value = ''
  if (cur?.requireText) nextTick(() => textInputRef.value?.focus())
})

const uid = Math.random().toString(36).slice(2, 8)
const cancelBtnRef = ref<HTMLButtonElement | null>(null)

function iconFor(k: ConfirmKind): string {
  switch (k) {
    case 'danger': return 'alert-triangle'
    case 'warning': return 'alert-circle'
    default: return 'help-circle'
  }
}

function resolve(value: boolean) {
  store.resolveCurrent(value)
}

function onBackdropClose(v: boolean) {
  // `requireExplicitClose` : on IGNORE le clic à l'extérieur (le dialogue ne se
  // ferme que par la croix / Annuler). On force juste l'état ouvert.
  if (!v) {
    if (requireExplicitClose.value) { open.value = true; return }
    resolve(false)
  }
}

// Escape = annuler (sauf requireExplicitClose) ; Enter = confirmer (si la saisie
// requise matche).
function onKey(e: KeyboardEvent) {
  if (!store.isOpen) return
  if (e.key === 'Escape' && !requireExplicitClose.value) { e.preventDefault(); resolve(false) }
  if (e.key === 'Enter' && canConfirm.value) { e.preventDefault(); resolve(true) }
}
onMounted(() => window.addEventListener('keydown', onKey))
onUnmounted(() => window.removeEventListener('keydown', onKey))
</script>

<style scoped>
.cd { max-width: 420px; }
.cd-head { display: flex; gap: 12px; align-items: center; }
.cd-x {
  margin-left: auto; flex-shrink: 0;
  width: 28px; height: 28px;
  display: inline-flex; align-items: center; justify-content: center;
  background: transparent; border: none; border-radius: var(--radius-sm);
  color: var(--text-muted); cursor: pointer;
}
.cd-x:hover { background: var(--surface-hover); color: var(--text); }
.cd-details {
  margin-top: 14px;
  border: 1px solid var(--border); border-radius: var(--radius);
  overflow: hidden;
}
.cd-detail {
  display: flex; justify-content: space-between; gap: 12px;
  padding: 8px 12px; font-size: 13px;
}
.cd-detail + .cd-detail { border-top: 1px solid var(--border); }
.cd-detail-k { color: var(--text-muted); }
.cd-detail-v { color: var(--text); font-weight: 600; text-align: right; }
.cd-icon {
  width: 36px; height: 36px;
  display: flex; align-items: center; justify-content: center;
  border-radius: var(--radius);
  background: var(--info-bg);
  color: var(--info);
  flex-shrink: 0;
}
.cd-icon[data-kind="danger"] { background: var(--danger-bg); color: var(--danger); }
.cd-icon[data-kind="warning"] { background: var(--warning-bg); color: var(--warning); }

h3 { margin: 0; font-size: 16px; font-weight: 600; color: var(--text); }
.cd-msg {
  margin: 14px 0 0;
  font-size: 13.5px;
  line-height: 1.5;
  color: var(--text-secondary);
}

.cd-require { margin-top: 16px; display: flex; flex-direction: column; gap: 6px; }
.cd-require label { font-size: 12.5px; color: var(--text-secondary); }
.cd-require label b { color: var(--text); font-family: var(--font-mono, monospace); }
.cd-require input {
  width: 100%; box-sizing: border-box;
  padding: 8px 10px;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  background: var(--surface);
  color: var(--text);
  font-size: 13.5px;
  outline: none;
}
.cd-require input:focus { border-color: var(--danger); }

.cd-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 20px;
}
.btn-primary:disabled { opacity: 0.45; cursor: not-allowed; }
.btn-secondary, .btn-primary {
  padding: 8px 14px;
  border-radius: var(--radius);
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid transparent;
  transition: background var(--duration-fast);
}
.btn-secondary {
  background: var(--surface);
  border-color: var(--border);
  color: var(--text-secondary);
}
.btn-secondary:hover { background: var(--surface-hover); }
.btn-primary {
  background: var(--primary);
  color: white;
}
.btn-primary:hover { background: var(--primary-hover); }
.btn-primary.danger { background: var(--danger); }
.btn-primary.danger:hover { background: #B91C1C; }

button:focus-visible {
  outline: 2px solid var(--primary);
  outline-offset: 2px;
}
</style>
